import {FC, useContext, useEffect, useMemo, useState} from 'react'
import classNames from 'classnames'
import {RenderIf, SSLErrorDetails, QuestionIcon} from 'components'
import {
  CustomAddBypassFromNotificationsPayload,
  SSLErrorDetailsType,
  SSLErrorItemType,
  SSLType,
  SelectedItemsTypeForActionButton,
  SliceStatus,
} from 'interfaces'
import {
  addCustomBypassToAllPoliciesFromNotifications,
  fetchNotifications,
} from './service'
import {useTranslation} from 'react-i18next'
import {ActionButton} from 'components/SSLErrorDetails/ActionButton'
import Toast from 'components/Basic/Toast'
import {sslErrorActions} from './reducer'
import {SSLErrorNotificationContext} from './context'
import {
  GET_APPLICATION_BYPASS_RESOURCE_QUERY_KEY,
  GET_URL_AND_APPLICATION_BYPASS_QUERY_KEY,
  GET_URL_BYPASS_RESOURCE_QUERY_KEY,
} from 'features/Policies/Bypass/hooks'
import {queryClient} from 'services'

interface NotificationSectionProps {
  isTopBarToBeBlueColor: boolean
}

const BYPASS_QUERY_KEYS_TO_INVALIDATE = [
  [GET_URL_BYPASS_RESOURCE_QUERY_KEY],
  [GET_APPLICATION_BYPASS_RESOURCE_QUERY_KEY],
  [GET_URL_AND_APPLICATION_BYPASS_QUERY_KEY],
]

const NotificationSection: FC<NotificationSectionProps> = ({
  isTopBarToBeBlueColor,
}) => {
  const {t} = useTranslation(['common'])
  const [sslErrorNotificationState, sslErrorActionDispatcher] = useContext(
    SSLErrorNotificationContext,
  )
  const [isUpdateBypassLoading, setIsUpdateBypassLoading] =
    useState<boolean>(false)
  const [componentMount, setComponentMount] = useState<boolean>(false)

  const {selectedSSLErrorView, status} = sslErrorNotificationState
  const notificationDetails = useMemo(() => {
    const {
      selectedSSLErrorView,
      byAppNotifications,
      byUrlNotifications,
      totalAppCount,
      totalUrlCount,
    } = sslErrorNotificationState
    const selectedViewData =
      selectedSSLErrorView === SSLType.app
        ? byAppNotifications
        : byUrlNotifications
    return {
      items: selectedViewData?.items,
      totalAppCount,
      totalUrlCount,
      offset: selectedViewData?.offset,
      sslType: sslErrorNotificationState.selectedSSLErrorView,
    }
  }, [sslErrorNotificationState])
  const {
    items,
    totalAppCount,
    totalUrlCount,
    offset: selectedSSLErrorViewOffset,
  } = notificationDetails
  const hasNotifications = totalAppCount > 0 || totalUrlCount > 0
  const hasSelectedViewCachedItems = items?.length > 0
  const isNotificationsLoading =
    status === SliceStatus.pending && !hasSelectedViewCachedItems

  const selectedItems = useMemo((): SelectedItemsTypeForActionButton => {
    const parentItems: string[] = []
    const childItems: string[] = []
    items
      ?.filter(
        item =>
          item.isSelected ||
          item.childItems.some(childItem => childItem.isSelected),
      )
      .forEach(item => {
        if (item.isSelected) {
          parentItems.push(item.processName)
        } else {
          item.childItems
            .filter(childItem => childItem.isSelected)
            .forEach(childItem => childItems.push(childItem.url))
        }
      })
    return {parentItems, childItems}
  }, [items])

  const areMultipleItemsSelected = useMemo(
    () =>
      items?.filter(
        item =>
          item.isSelected ||
          item?.childItems?.some(childItem => childItem.isSelected) ||
          false,
      ).length > 1,
    [items],
  )

  useEffect(() => {
    setComponentMount(true)

    if (hasNotifications) {
      // fetch notifications by url, in background
      getNotificationsOnViewChange(0, SSLType.url)
    }
  }, [])

  useEffect(() => {
    if (componentMount) {
      getNotificationsOnViewChange()
    }

    return () => {
      if (selectedSSLErrorView === SSLType.url) {
        sslErrorActionDispatcher(
          sslErrorActions.setSelectedSSLErrorViewType(SSLType.app),
        )
      }
    }
  }, [selectedSSLErrorView])

  const updateByPass = async (
    bypassPayload: CustomAddBypassFromNotificationsPayload,
  ): Promise<void> => {
    try {
      setIsUpdateBypassLoading(true)
      await addCustomBypassToAllPoliciesFromNotifications(bypassPayload)
      await Promise.all(
        BYPASS_QUERY_KEYS_TO_INVALIDATE.map(queryKey =>
          queryClient.invalidateQueries({
            queryKey,
            refetchType: 'active',
          }),
        ),
      )

      // Fetching notifications again
      sslErrorActionDispatcher(sslErrorActions.getSSLErrorNotifications())
      const result = await fetchNotifications({
        offset: 0,
        type: selectedSSLErrorView,
      })
      Toast({message: t('BYPASS_UPDATED_SUCCESSFULLY'), type: 'success'})
      setIsUpdateBypassLoading(false)
      if (result) {
        sslErrorActionDispatcher(
          sslErrorActions.getSSLErrorNotificationsSuccess(result),
        )
      }
    } catch (err) {
      setIsUpdateBypassLoading(false)
    }
  }

  const getNotificationsOnViewChange = async (
    offset: number = selectedSSLErrorViewOffset,
    type: SSLType = selectedSSLErrorView,
  ): Promise<void> => {
    sslErrorActionDispatcher(sslErrorActions.getSSLErrorNotifications())
    const result = await fetchNotifications({
      offset,
      type,
    })
    const tmpObj: SSLErrorDetailsType = {
      ...result,
      items: !selectedSSLErrorViewOffset
        ? result.items
        : [...notificationDetails.items, ...result.items],
    }
    sslErrorActionDispatcher(
      sslErrorActions.getSSLErrorNotificationsSuccess(tmpObj),
    )
  }

  const handleSSLErrorItemsStateChange = (
    updatedErrorItems: SSLErrorItemType[],
  ): void => {
    sslErrorActionDispatcher(
      sslErrorActions.getSSLErrorNotificationsSuccess({
        ...notificationDetails,
        items: updatedErrorItems,
      }),
    )
  }

  const setSelectedSSLErrorViewType = (type: SSLType): void => {
    sslErrorActionDispatcher(sslErrorActions.setSelectedSSLErrorViewType(type))
  }

  return (
    <div
      id="notification-section"
      className={classNames(
        'border-l-0.5 border-b-0.5 border-r-0.5 border-gray-400 z-50 absolute right-36 w-96 h-auto max-h-96 py-4',
        {
          'bg-dope-black': !isTopBarToBeBlueColor,
          'bg-dope-teal-100': isTopBarToBeBlueColor,
        },
      )}
    >
      <label
        data-cy="notification-heading"
        className="karla-bold text-white text-sm px-6"
      >
        {t('NOTIFICATIONS')}
      </label>
      <RenderIf isTrue={hasNotifications || isNotificationsLoading}>
        <>
          <div data-cy="notification-description" className="text-xs py-4 px-6">
            {/* i18next: check how to add markup in externalization message. */}
            <p className="karla-exl text-gray-1600">
              SSL pinning errors detected. Resolve them by bypassing
            </p>
            <p className="flex items-center karla-exl text-gray-1600">
              applications or URLs below.
              <a
                href="https://inflight.dope.security/dope.console/notifications"
                target="_blank"
              >
                <QuestionIcon
                  strokeColor="text-white/40 ml-1"
                  strokeWidth={1}
                  width={14}
                  height={14}
                  classes="hover:text-white/70"
                />
              </a>
            </p>
          </div>
          <SSLErrorDetails
            selectedSSLErrorViewType={selectedSSLErrorView}
            setSelectedSSLErrorViewType={setSelectedSSLErrorViewType}
            notificationDetails={notificationDetails}
            handleChange={handleSSLErrorItemsStateChange}
            fetchData={getNotificationsOnViewChange}
            isNotificationsLoading={isNotificationsLoading}
            isUpdateBypassLoading={isUpdateBypassLoading}
            updateByPass={updateByPass}
            areMultipleItemsSelected={areMultipleItemsSelected}
          />
        </>
      </RenderIf>
      <RenderIf isTrue={!hasNotifications && !isNotificationsLoading}>
        <p className="karla-exl text-gray-1600 text-xs mt-4 px-6">
          {t('NO_NOTIFICATIONS')}
        </p>
      </RenderIf>
      <RenderIf isTrue={areMultipleItemsSelected}>
        <div className="absolute bottom-0 w-full bg-inherit">
          <div className="mt-2"></div>
          <ActionButton
            isByAppViewSelected={selectedSSLErrorView === SSLType.app}
            selectedItems={selectedItems}
            handler={updateByPass}
            isLoading={isUpdateBypassLoading}
          />
        </div>
      </RenderIf>
    </div>
  )
}

export default NotificationSection
